import api from './api';
import {
  Zone,
  Facility,
  RateCard,
  DriverType,
  SalaryExtraFee,
  BobtailQuote,
  BobtailQuoteRequest
} from '../types/salary-module';

// ── snake_case → camelCase transform helpers ──

function transformZone(raw: Record<string, unknown>): Zone {
  return {
    id: raw.id,
    name: raw.name,
    code: raw.code || undefined,
    description: raw.description || undefined,
    itemCount: Number(raw.item_count ?? 0),
  };
}

function transformFacility(raw: Record<string, unknown>): Facility {
  const facilityType = (raw.facility_type || '').toLowerCase();
  return {
    id: raw.facility_id,
    type: facilityType.toUpperCase() as Facility['type'],
    facilityType,
    name: raw.name,
    address: raw.address || undefined,
    city: raw.city || undefined,
    state: raw.state || undefined,
    phone: raw.phone || undefined,
    email: raw.email || undefined,
    zoneId: raw.zone_id ?? null,
  };
}

function transformSalaryExtraFee(raw: Record<string, unknown>): SalaryExtraFee {
  return {
    id: raw.id as number,
    category: raw.category as string,
    name: raw.name as string,
    driverType: raw.driver_type as DriverType,
    feeType: raw.fee_type as 'flat' | 'per_unit',
    amount: Number(raw.amount ?? 0),
    unitValue: Number(raw.unit_value ?? 1),
    unitType: (raw.unit_type as string) ?? null,
    graceValue: Number(raw.grace_value ?? 0),
    graceUnit: (raw.grace_unit as string) ?? 'hour',
    isSystem: (raw.is_system as boolean) ?? false,
    notes: (raw.notes as string) ?? null,
    createdAt: raw.created_at as string,
    updatedAt: raw.updated_at as string,
  };
}

function transformRateCard(raw: Record<string, unknown>): RateCard {
  return {
    id: raw.id,
    status: raw.status,
    driverType: raw.driver_type,
    originZoneId: raw.origin_zone_id,
    destinationZoneId: raw.destination_zone_id,
    originZoneName: raw.origin_zone_name || undefined,
    destinationZoneName: raw.destination_zone_name || undefined,
    taskType: raw.task_type,
    tripType: raw.trip_type || undefined,
    containerSize: raw.container_size || undefined,
    baseRate: Number(raw.base_rate),
    overweightAmount: raw.overweight_amount != null ? Number(raw.overweight_amount) : undefined,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

// ── API object ──

export const salaryModuleApi = {
  zones: {
    async getAll(): Promise<Zone[]> {
      const res = await api.get('/zones');
      return (res.data as Record<string, unknown>[]).map(transformZone);
    },

    async create(zone: Omit<Zone, 'id'>): Promise<Zone> {
      const res = await api.post('/zones', zone);
      return transformZone(res.data);
    },

    async update(id: number, updates: Partial<Zone>): Promise<Zone> {
      const res = await api.patch(`/zones/${id}`, updates);
      return transformZone(res.data);
    },

    async delete(id: number): Promise<void> {
      await api.delete(`/zones/${id}`);
    },
  },

  facilities: {
    async getAll(filters?: { type?: string; keyword?: string }): Promise<Facility[]> {
      const params: Record<string, string> = {};
      if (filters?.type && filters.type !== 'ALL') params.type = filters.type;
      if (filters?.keyword) params.keyword = filters.keyword;
      const res = await api.get('/zones/facilities', { params });
      return (res.data as Record<string, unknown>[]).map(transformFacility);
    },

    async assignZone(facilityId: number, facilityType: string, zoneId: number | null): Promise<void> {
      if (zoneId === null) {
        await api.post('/zones/unassign-facility', {
          facility_type: facilityType,
          facility_id: facilityId,
        });
      } else {
        await api.post('/zones/assign-facility', {
          zone_id: zoneId,
          facility_type: facilityType,
          facility_id: facilityId,
        });
      }
    },
  },

  salaryExtraFees: {
    async getAll(filters?: { category?: string; driver_type?: string }): Promise<SalaryExtraFee[]> {
      const params: Record<string, string> = {};
      if (filters?.category) params.category = filters.category;
      if (filters?.driver_type) params.driver_type = filters.driver_type;
      const res = await api.get('/salary-extra-fees', { params });
      return (res.data as Record<string, unknown>[]).map(transformSalaryExtraFee);
    },

    async getById(id: number): Promise<SalaryExtraFee> {
      const res = await api.get(`/salary-extra-fees/${id}`);
      return transformSalaryExtraFee(res.data);
    },

    async create(data: Partial<SalaryExtraFee>): Promise<SalaryExtraFee> {
      const res = await api.post('/salary-extra-fees', {
        category: data.category,
        name: data.name,
        driver_type: data.driverType,
        fee_type: data.feeType ?? 'flat',
        amount: data.amount ?? 0,
        unit_value: data.unitValue ?? 1,
        unit_type: data.unitType ?? null,
        grace_value: data.graceValue ?? 0,
        grace_unit: data.graceUnit ?? 'hour',
        notes: data.notes ?? null,
      });
      return transformSalaryExtraFee(res.data);
    },

    async update(id: number, data: Partial<SalaryExtraFee>): Promise<SalaryExtraFee> {
      const body: Record<string, unknown> = {};
      if (data.name !== undefined) body.name = data.name;
      if (data.feeType !== undefined) body.fee_type = data.feeType;
      if (data.amount !== undefined) body.amount = data.amount;
      if (data.unitValue !== undefined) body.unit_value = data.unitValue;
      if (data.unitType !== undefined) body.unit_type = data.unitType;
      if (data.graceValue !== undefined) body.grace_value = data.graceValue;
      if (data.graceUnit !== undefined) body.grace_unit = data.graceUnit;
      if (data.notes !== undefined) body.notes = data.notes;
      const res = await api.patch(`/salary-extra-fees/${id}`, body);
      return transformSalaryExtraFee(res.data);
    },

    async delete(id: number): Promise<void> {
      await api.delete(`/salary-extra-fees/${id}`);
    },
  },

  rateCards: {
    async getAll(filters?: {
      driverType?: DriverType;
      zoneId?: string | number;
      taskType?: string;
      status?: 'ACTIVE' | 'INACTIVE';
    }): Promise<{ items: RateCard[]; total: number }> {
      const params: Record<string, string> = {};
      if (filters?.driverType) params.driver_type = filters.driverType;
      if (filters?.zoneId) params.zone_id = String(filters.zoneId);
      if (filters?.taskType) params.task_type = filters.taskType;
      if (filters?.status) params.status = filters.status;
      const res = await api.get('/salary-rates', { params });
      // Response interceptor strips `success` but keeps `items` + `total`
      const data = res.data as { items?: Record<string, unknown>[]; total?: number };
      const items = (data.items || []).map(transformRateCard);
      return { items, total: data.total ?? items.length };
    },

    async create(rateCard: Omit<RateCard, 'id' | 'createdAt' | 'updatedAt'>): Promise<RateCard> {
      const res = await api.post('/salary-rates', {
        status: rateCard.status,
        driver_type: rateCard.driverType,
        origin_zone_id: rateCard.originZoneId,
        destination_zone_id: rateCard.destinationZoneId,
        task_type: rateCard.taskType,
        trip_type: rateCard.tripType,
        container_size: rateCard.containerSize,
        base_rate: rateCard.baseRate,
        overweight_amount: rateCard.overweightAmount,
      });
      return transformRateCard(res.data);
    },

    async update(id: number, updates: Partial<RateCard>): Promise<RateCard> {
      const body: Record<string, unknown> = {};
      if (updates.status !== undefined) body.status = updates.status;
      if (updates.driverType !== undefined) body.driver_type = updates.driverType;
      if (updates.originZoneId !== undefined) body.origin_zone_id = updates.originZoneId;
      if (updates.destinationZoneId !== undefined) body.destination_zone_id = updates.destinationZoneId;
      if (updates.taskType !== undefined) body.task_type = updates.taskType;
      if (updates.tripType !== undefined) body.trip_type = updates.tripType;
      if (updates.containerSize !== undefined) body.container_size = updates.containerSize;
      if (updates.baseRate !== undefined) body.base_rate = updates.baseRate;
      if (updates.overweightAmount !== undefined) body.overweight_amount = updates.overweightAmount;
      const res = await api.patch(`/salary-rates/${id}`, body);
      return transformRateCard(res.data);
    },

    async delete(id: number): Promise<void> {
      await api.delete(`/salary-rates/${id}`);
    },

    async duplicate(id: number): Promise<RateCard> {
      const res = await api.post(`/salary-rates/${id}/duplicate`);
      return transformRateCard(res.data);
    },
  },

  /**
   * 派单板 BT 徽标问价：这几段空驶按工资费率卡各该付多少。
   *
   * 服务端算，因为匹配有通配/打分/AB-BA 回退，还要按「谁领这笔钱」拆开
   * （公司司机开老板的车是两笔，板上显示的是总数）——抄到前端必然走样。
   */
  async quoteBobtail(pairs: BobtailQuoteRequest[]): Promise<BobtailQuote[]> {
    if (pairs.length === 0) return [];
    const res = await api.post('/salary-rates/bobtail-quotes', { pairs });
    return (res.data as Record<string, unknown>[]).map(raw => ({
      driverId: Number(raw.driverId),
      fromZoneId: Number(raw.fromZoneId),
      toZoneId: Number(raw.toZoneId),
      // null = 一档都没配价。别在这里塞 0 —— 0 会把「忘了配费率」说成
      // 「这趟本来就不给钱」，而那正是司机白跑的那种情况。
      amount: raw.amount == null ? null : Number(raw.amount),
      breakdown: ((raw.breakdown || []) as Record<string, unknown>[]).map(b => ({
        role: String(b.role),
        driverType: String(b.driverType) as DriverType,
        taskType: String(b.taskType),
        amount: Number(b.amount),
      })),
      unpricedRoles: ((raw.unpricedRoles || []) as Record<string, unknown>[]).map(u => ({
        role: String(u.role),
        driverType: String(u.driverType) as DriverType,
      })),
    }));
  },
};
